import { useState, useEffect, useRef } from 'react';

export const useAudioAnalyzer = (vibeConfig) => {
  const [audioData, setAudioData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioContextRef = useRef(null);
  const analyzerRef = useRef(null);
  const sourceRef = useRef(null);
  const animationRef = useRef(null);

  const initializeAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyzerRef.current = audioContextRef.current.createAnalyser();
      
      const audioConfig = vibeConfig?.audio || {};
      analyzerRef.current.fftSize = 1024; // Even higher resolution for better treble detection
      analyzerRef.current.smoothingTimeConstant = audioConfig.smoothing || 0.6; // Even less smoothing for maximum responsiveness
      
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyzerRef.current);
      
      setIsPlaying(true);
      startAnalyzing();
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    }
  };

  const startAnalyzing = () => {
    const bufferLength = analyzerRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const analyze = () => {
      analyzerRef.current.getByteFrequencyData(dataArray);
      
      const sensitivity = vibeConfig?.audio?.sensitivity || 1.5; // Increased default sensitivity
      const processedData = Array.from(dataArray).map(value => (value / 255) * sensitivity);
      
      // Calculate more responsive audio metrics with optimized frequency ranges
      const average = processedData.reduce((a, b) => a + b, 0) / processedData.length;
      const peak = Math.max(...processedData);
      
      // Optimized frequency ranges for better treble detection
      const bass = processedData.slice(0, 32).reduce((a, b) => a + b, 0) / 32; // 0-1.5kHz
      const mid = processedData.slice(32, 128).reduce((a, b) => a + b, 0) / 96; // 1.5-6kHz  
      const treble = processedData.slice(128).reduce((a, b) => a + b, 0) / (processedData.length - 128); // 6kHz+
      
      // Also calculate high treble for extra sensitivity
      const highTreble = processedData.slice(256).reduce((a, b) => a + b, 0) / (processedData.length - 256); // 12kHz+
      
      setAudioData({
        frequency: processedData,
        average: Math.pow(average, 1.5),
        peak: Math.pow(peak, 1.2),
        bass: Math.pow(bass, 1.3),
        mid: Math.pow(mid, 1.2),
        treble: Math.pow((treble * 4 + highTreble * 6), 1.8) // Massive treble boost with high frequency emphasis
      });

      animationRef.current = requestAnimationFrame(analyze);
    };

    analyze();
  };

  const stopAudio = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    setIsPlaying(false);
    setAudioData(null);
  };

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  return {
    audioData,
    isPlaying,
    initializeAudio,
    stopAudio
  };
};